import { Button, CheckIcon, CloseIcon, Table, TableColumns, Tooltip, UpdateIcon } from '@bp/ui-components';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { EventJobStatus, SortDirection, useEventJobsQuery } from '../../../types/planung-graphql-client-defs';

type SynchronizationsTableType = {
  uuid: string;
  triggeredAt: string;
  status: EventJobStatus;
};

type SynchronizationsTableProps = {
  onSync: () => void;
};

export const SynchronizationsTable = ({ onSync }: SynchronizationsTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const userConfigContext = useUserConfigContext();
  const schoolYear = userConfigContext.selectedSchoolYear;

  const [{ data: eventJobsData }, refetch] = useEventJobsQuery({
    variables: {
      where: {
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      options: {
        sort: [{ createdAt: SortDirection.Desc }],
      },
    },
    pause: !schoolYear,
    requestPolicy: 'network-only',
  });

  const hasRunningEventJobs = eventJobsData?.eventJobs.some((job) => job.status === EventJobStatus.Running);

  const data: SynchronizationsTableType[] =
    eventJobsData?.eventJobs.map((job) => {
      return {
        uuid: job.uuid,
        triggeredAt: dayjs(job.createdAt).format('DD.MM.YYYY, HH:mm'),
        status: job.status,
      };
    }) ?? [];

  const columns: TableColumns<SynchronizationsTableType>[] = useMemo(() => {
    function getStatusTranslation(status: EventJobStatus): { text: string; icon: ReactNode } {
      switch (status) {
        case EventJobStatus.Error:
          return { text: t('synchronizations.error'), icon: <CloseIcon color='var(--color-error)' /> };
        case EventJobStatus.Finished:
          return { text: t('synchronizations.finished'), icon: <CheckIcon color='var(--color-success)' /> };
        case EventJobStatus.Running:
          return { text: t('synchronizations.running'), icon: <UpdateIcon className='svg-icon spinning' /> };
        default:
          return { text: '', icon: <></> };
      }
    }

    return [
      {
        header: t('synchronizations.triggeredAt'),
        accessorKey: 'triggeredAt',
        id: 'triggeredAt',
      },
      {
        header: t('common.status'),
        accessorKey: 'status',
        id: 'status',
        size: 150,
        cell: ({ row }) => {
          const { text, icon } = getStatusTranslation(row.original.status);
          return (
            <Tooltip triggerStyle={{ display: 'inline' }} content={text}>
              {icon}
            </Tooltip>
          );
        },
      },
    ];
  }, [t]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refetch();
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetch]);

  return (
    <Table<SynchronizationsTableType>
      columns={columns}
      data={data}
      showBorderRadius
      showShadow
      isOnWhite={false}
      showActionBar
      actionBarSettings={{
        extendedActionsRight: (
          <Button onClick={onSync} icon={<UpdateIcon />} disabled={hasRunningEventJobs}>
            {t('synchronizations.syncNow')}
          </Button>
        ),
      }}
    />
  );
};
